import { cn } from '../../lib/cn'
import s from './PipelineBar.module.css'

/**
 * Le funnel d'un lot ou d'un client — report de `funnelBar` du prototype validé
 * (`mocks/ghp-bo/screens.js:1719`) : les cinq étapes côte à côte, proportionnelles.
 *
 * ⚠️ **La barre porte une phrase, pas seulement des couleurs.** Cinq segments accolés n'ont aucun
 * texte : sans `aria-label`, un lecteur d'écran n'annonce rien, et l'avancement est précisément ce
 * que la fiche client existe pour montrer. Le prototype s'en tire avec un `title` par segment —
 * que la synthèse vocale ne lit pas, et que le doigt n'atteint jamais.
 *
 * ⚠️ **Les parts se calculent ici, pas au serveur.** Le serveur compte des contrôles (D24) ; une
 * proportion est une décision d'affichage, et la faire calculer en base obligerait à la recompter
 * à chaque changement d'épaisseur.
 */

export type PipelineTone = 'neutral' | 'warn' | 'info' | 'processing' | 'ok'

export interface PipelineStage {
  key: string
  label: string
  value: number
  tone: PipelineTone
}

export interface PipelineBarProps {
  /** Ce que la barre mesure — « Avancement du lot A ». Ouvre la phrase annoncée. */
  label: string
  stages: readonly PipelineStage[]
  /** L'épaisseur réduite du prototype, pour la ligne d'un lot. */
  thin?: boolean
  className?: string
}

export function PipelineBar({ label, stages, thin, className }: PipelineBarProps) {
  const filled = stages.filter((stage) => stage.value > 0)
  const total = filled.reduce((sum, stage) => sum + stage.value, 0)

  // `Aucun contrôle` plutôt qu'une liste vide : un client qu'on vient de créer a une barre, et
  // « Avancement : » suivi de rien inquiète le lecteur plus qu'il ne l'informe (M21).
  const spoken =
    total === 0
      ? `${label} : aucun contrôle`
      : `${label} : ${filled.map((stage) => `${stage.label} : ${String(stage.value)}`).join(', ')}`

  return (
    <div className={cn(s.root, thin === true && s.thin, className)} role="img" aria-label={spoken}>
      {filled.map((stage) => (
        <span
          key={stage.key}
          className={s.segment}
          data-stage={stage.key}
          data-tone={stage.tone}
          style={{ '--stage-share': `${((stage.value / total) * 100).toFixed(1)}%` } as never}
        />
      ))}
    </div>
  )
}
